// components/CalendarioView.tsx
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaCalendarPlus, 
  FaCalendarAlt, 
  FaFilm, 
  FaTv,
  FaLaugh,
  FaPlus,
  FaCheck,
  FaTimes,
  FaEdit,
  FaTrash,
  FaStar,
  FaRegStar,
  FaEye,
  FaRegEye,
  FaClock,
  FaExclamationTriangle,
  FaHeart,
  FaSave,
  FaArrowLeft
} from 'react-icons/fa';
import { useTodoLists } from '../hooks/useTodoLists';
import { CalendarEvent, MovieSeriesItem, FunnyWord } from '../types/calendar';
import { CalendarModal } from '../components/CalendarModal';

type ViewMode = 'overview' | 'calendar';

interface CalendarioViewProps {
  userId: string;
  userName: string;
}

export const CalendarioView = ({ userId, userName }: CalendarioViewProps) => {
  const [viewMode, setViewMode] = useState<ViewMode>('overview');
  const [showEventModal, setShowEventModal] = useState(false);
  const [showMovieModal, setShowMovieModal] = useState(false);
  const [showWordModal, setShowWordModal] = useState(false);
  const [showMovieListModal, setShowMovieListModal] = useState(false);
  const [showWordListModal, setShowWordListModal] = useState(false);
  const [editingMovie, setEditingMovie] = useState<string | null>(null);
  const [editingWord, setEditingWord] = useState<string | null>(null);
  
  // Form states
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    type: 'event' as CalendarEvent['type'],
    priority: 'medium' as CalendarEvent['priority']
  });

  const [movieForm, setMovieForm] = useState({
    title: '',
    type: 'movie' as MovieSeriesItem['type'],
    genre: '',
    platform: '',
    notes: ''
  });

  const [wordForm, setWordForm] = useState({
    word: '',
    context: ''
  });

  // Edit forms
  const [editMovieForm, setEditMovieForm] = useState<MovieSeriesItem | null>(null);
  const [editWordForm, setEditWordForm] = useState<FunnyWord | null>(null);

  const {
    todoLists,
    isLoading,
    addCalendarEvent,
    updateCalendarEvent,
    deleteCalendarEvent,
    addMovieSeries,
    updateMovieSeries,
    deleteMovieSeries,
    addFunnyWord,
    updateFunnyWord,
    deleteFunnyWord,
    getUpcomingEvents,
    getWatchListStats
  } = useTodoLists(userId, userName);

  // Handlers para eventos
  const handleAddEvent = async () => {
    if (!eventForm.title.trim() || !eventForm.date) return;
    
    await addCalendarEvent(eventForm);
    setEventForm({
      title: '',
      description: '',
      date: '',
      time: '',
      type: 'event',
      priority: 'medium'
    });
    setShowEventModal(false);
  };

  const handleAddMovie = async () => {
    if (!movieForm.title.trim()) return;
    
    await addMovieSeries(movieForm);
    setMovieForm({
      title: '',
      type: 'movie',
      genre: '',
      platform: '',
      notes: ''
    });
    setShowMovieModal(false);
  };

  const handleAddWord = async () => {
    if (!wordForm.word.trim()) return;
    
    await addFunnyWord(wordForm);
    setWordForm({
      word: '',
      context: ''
    });
    setShowWordModal(false);
  };

  // Edit handlers
  const handleEditMovie = (movie: MovieSeriesItem) => {
    setEditMovieForm(movie);
    setEditingMovie(movie.id);
  };

  const handleSaveMovie = async () => {
    if (!editMovieForm || !editMovieForm.title.trim()) return;
    
    const { id, ...updateData } = editMovieForm;
    await updateMovieSeries(id, updateData);
    setEditMovieForm(null);
    setEditingMovie(null);
  };

  const handleEditWord = (word: FunnyWord) => {
    setEditWordForm(word);
    setEditingWord(word.id);
  };

  const handleSaveWord = async () => {
    if (!editWordForm || !editWordForm.word.trim()) return;
    
    const { id, ...updateData } = editWordForm;
    await updateFunnyWord(id, updateData);
    setEditWordForm(null);
    setEditingWord(null);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { 
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  const upcomingEvents = getUpcomingEvents(7);
  const watchStats = getWatchListStats();

  if (viewMode === 'calendar') {
    return (
      <CalendarModal 
        events={todoLists.calendarEvents}
        onBack={() => setViewMode('overview')}
        onAddEvent={addCalendarEvent}
        onUpdateEvent={updateCalendarEvent}
        onDeleteEvent={deleteCalendarEvent}
      />
    );
  }

  return (
    <div className="h-full flex flex-col  relative overflow-hidden">

      {/* Contenido principal */}
      <div className="flex-1 overflow-auto p-4 py-6 space-y-4">
        
        {/* Lista 1: Eventos y Calendario */}
        <div className="bg-white/20 backdrop-blur-sm rounded-2xl shadow-lg border border-white/10 p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl text-white">
                <FaCalendarAlt className="w-4 h-4" />
              </div>
              <div>
                <h3 className="font-bold text-secondary">Eventos y Fechas</h3>
                <p className="text-xs text-gray-200">{upcomingEvents.length} próximos</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode('calendar')}
                className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                title="Ver calendario completo"
              >
                <FaCalendarAlt className="w-4 h-4" />
              </button>
              <button
                onClick={() => setShowEventModal(true)}
                className="p-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg hover:from-blue-600 hover:to-purple-600 transition-colors"
              >
                <FaPlus className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="space-y-2 max-h-40 overflow-y-auto">
            {upcomingEvents.slice(0, 4).map(event => (
              <div key={event.id} className="flex items-center gap-3 p-2 bg-white/10 rounded-lg">
                <div className={`w-2 h-2 rounded-full ${
                  event.priority === 'high' ? 'bg-red-400' :
                  event.priority === 'medium' ? 'bg-yellow-400' : 'bg-green-400'
                }`} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-secondary text-sm truncate">{event.title}</p>
                  <p className="text-xs text-gray-200">{formatDate(event.date)} {event.time && `• ${event.time}`}</p>
                </div>
                <button
                  onClick={() => updateCalendarEvent(event.id, { completed: !event.completed })}
                  className="p-1 text-green-500 hover:bg-green-100 rounded transition-colors"
                >
                  {event.completed ? <FaCheck className="w-3 h-3" /> : <FaClock className="w-3 h-3" />}
                </button>
              </div>
            ))}
            {upcomingEvents.length === 0 && (
              <div className="text-center py-4 text-gray-400">
                <FaCalendarAlt className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No hay eventos próximos</p>
              </div>
            )}
          </div>
        </div>

        {/* Lista 2: Películas y Series */}
        <div className="bg-white/20 backdrop-blur-sm rounded-2xl shadow-lg border border-white/10 p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-r from-red-500 to-orange-500 rounded-xl text-white">
                <FaFilm className="w-4 h-4" />
              </div>
              <div>
                <h3 className="font-bold text-secondary">Por Ver</h3>
                <p className="text-xs text-gray-200">
                  {watchStats.pending} pendientes • {watchStats.watched} vistas
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowMovieListModal(true)}
                className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                title="Gestionar lista"
              >
                <FaEdit className="w-4 h-4" />
              </button>
              <button
                onClick={() => setShowMovieModal(true)}
                className="p-2 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-lg hover:from-red-600 hover:to-orange-600 transition-colors"
              >
                <FaPlus className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="space-y-2 max-h-40 overflow-y-auto">
            {todoLists.moviesSeries.map(item => (
              <div key={item.id} className="flex items-center gap-3 p-2 bg-white/10 rounded-lg">
                <div className="text-lg">
                  {item.type === 'movie' ? '🎬' : '📺'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-secondary text-sm truncate">{item.title}</p>
                  <div className="flex items-center gap-2 text-xs text-gray-200">
                    {item.genre && <span>{item.genre}</span>}
                    {item.platform && <span>• {item.platform}</span>}
                  </div>
                </div>
                <button
                  onClick={() => updateMovieSeries(item.id, { watched: !item.watched })}
                  className={`p-1 rounded transition-colors ${
                    item.watched 
                      ? 'text-green-500 hover:bg-green-100' 
                      : 'text-gray-400 hover:bg-gray-100'
                  }`}
                >
                  {item.watched ? <FaEye className="w-3 h-3" /> : <FaRegEye className="w-3 h-3" />}
                </button>
              </div>
            ))}
            {todoLists.moviesSeries.length === 0 && (
              <div className="text-center py-4 text-gray-400">
                <FaFilm className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No hay películas o series agregadas</p>
              </div>
            )}
          </div>
        </div>

        {/* Lista 3: Palabras Graciosas */}
        <div className="bg-white/20 backdrop-blur-sm rounded-2xl shadow-lg border border-white/10 p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl text-white">
                <FaLaugh className="w-4 h-4" />
              </div>
              <div>
                <h3 className="font-bold text-secondary">Silly Words</h3>
                <p className="text-xs text-gray-200">{todoLists.funnyWords.length} palabras</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowWordListModal(true)}
                className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                title="Gestionar lista"
              >
                <FaEdit className="w-4 h-4" />
              </button>
              <button
                onClick={() => setShowWordModal(true)}
                className="p-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg hover:from-yellow-600 hover:to-orange-600 transition-colors"
              >
                <FaPlus className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
            {todoLists.funnyWords.slice(0, 12).map(word => (
              <div
                key={word.id}
                className="flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-yellow-100 to-orange-100 text-gray-800 rounded-full text-sm border border-yellow-200"
              >
                <span className="truncate max-w-20">{word.word}</span>
                <button
                  onClick={() => updateFunnyWord(word.id, { favorite: !word.favorite })}
                  className="text-yellow-600 hover:text-yellow-700"
                >
                  {word.favorite ? <FaHeart className="w-3 h-3" /> : <FaRegStar className="w-3 h-3" />}
                </button>
              </div>
            ))}
            {todoLists.funnyWords.length === 0 && (
              <div className="w-full text-center py-4 text-gray-400">
                <FaLaugh className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No hay palabras graciosas aún</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de gestión de películas/series */}
      <AnimatePresence>
        {showMovieListModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white/20 backdrop-blur-md rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] shadow-2xl border border-white/10"
            >
              <div className="flex items-center gap-3 mb-6">
                <button
                  onClick={() => setShowMovieListModal(false)}
                  className="p-2 bg-gray-200/80 text-gray-700 rounded-lg hover:bg-gray-300/80 transition-colors"
                >
                  <FaArrowLeft className="w-4 h-4" />
                </button>
                <h3 className="text-xl font-bold bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">
                  🎬 Gestionar Lista
                </h3>
                <span className="text-sm text-gray-400">({todoLists.moviesSeries.length} elementos)</span>
              </div>
              
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {todoLists.moviesSeries.map(item => (
                  <div key={item.id} className="bg-white/10 rounded-lg p-4">
                    {editingMovie === item.id && editMovieForm ? (
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={editMovieForm.title}
                          onChange={(e) => setEditMovieForm({ ...editMovieForm, title: e.target.value })}
                          className="w-full p-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 bg-white/80"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <select
                            value={editMovieForm.type}
                            onChange={(e) => setEditMovieForm({ ...editMovieForm, type: e.target.value as MovieSeriesItem['type'] })}
                            className="p-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 bg-white/80"
                          >
                            <option value="movie">Película</option>
                            <option value="series">Serie</option>
                          </select>
                          <input
                            type="text"
                            placeholder="Género"
                            value={editMovieForm.genre || ''}
                            onChange={(e) => setEditMovieForm({ ...editMovieForm, genre: e.target.value })}
                            className="p-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 bg-white/80"
                          />
                        </div>
                        <input
                          type="text"
                          placeholder="Plataforma"
                          value={editMovieForm.platform || ''}
                          onChange={(e) => setEditMovieForm({ ...editMovieForm, platform: e.target.value })}
                          className="w-full p-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 bg-white/80"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={handleSaveMovie}
                            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
                          >
                            <FaSave className="w-3 h-3" />
                            Guardar
                          </button>
                          <button
                            onClick={() => {
                              setEditingMovie(null);
                              setEditMovieForm(null);
                            }}
                            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <div className="text-2xl">
                          {item.type === 'movie' ? '🎬' : '📺'}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-secondary">{item.title}</h4>
                          <div className="flex items-center gap-2 text-sm text-gray-300">
                            <span className="capitalize">{item.type}</span>
                            {item.genre && <span>• {item.genre}</span>}
                            {item.platform && <span>• {item.platform}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateMovieSeries(item.id, { watched: !item.watched })}
                            className={`p-2 rounded-lg transition-colors ${
                              item.watched 
                                ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            }`}
                          >
                            {item.watched ? <FaEye className="w-4 h-4" /> : <FaRegEye className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => handleEditMovie(item)}
                            className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                          >
                            <FaEdit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteMovieSeries(item.id)}
                            className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                          >
                            <FaTrash className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {todoLists.moviesSeries.length === 0 && (
                  <div className="text-center py-8 text-gray-400">
                    <FaFilm className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No hay películas o series agregadas</p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal de gestión de palabras graciosas */}
      <AnimatePresence>
        {showWordListModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white/20 backdrop-blur-md rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] shadow-2xl border border-white/10"
            >
              <div className="flex items-center gap-3 mb-6">
                <button
                  onClick={() => setShowWordListModal(false)}
                  className="p-2 bg-gray-200/80 text-gray-700 rounded-lg hover:bg-gray-300/80 transition-colors"
                >
                  <FaArrowLeft className="w-4 h-4" />
                </button>
                <h3 className="text-xl font-bold bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent">
                  😄 Gestionar Palabras
                </h3>
                <span className="text-sm text-gray-400">({todoLists.funnyWords.length} elementos)</span>
              </div>
              
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {todoLists.funnyWords.map(word => (
                  <div key={word.id} className="bg-white/10 rounded-lg p-4">
                    {editingWord === word.id && editWordForm ? (
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={editWordForm.word}
                          onChange={(e) => setEditWordForm({ ...editWordForm, word: e.target.value })}
                          className="w-full p-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-white/80"
                        />
                        <textarea
                          placeholder="Contexto"
                          value={editWordForm.context || ''}
                          onChange={(e) => setEditWordForm({ ...editWordForm, context: e.target.value })}
                          className="w-full p-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 bg-white/80 h-20 resize-none"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={handleSaveWord}
                            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
                          >
                            <FaSave className="w-3 h-3" />
                            Guardar
                          </button>
                          <button
                            onClick={() => {
                              setEditingWord(null);
                              setEditWordForm(null);
                            }}
                            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-3">
                        <div className="text-2xl mt-1">😄</div>
                        <div className="flex-1">
                          <h4 className="font-medium text-secondary">{word.word}</h4>
                          {word.context && (
                            <p className="text-sm text-gray-300 mt-1">{word.context}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateFunnyWord(word.id, { favorite: !word.favorite })}
                            className={`p-2 rounded-lg transition-colors ${
                              word.favorite 
                                ? 'bg-red-100 text-red-500 hover:bg-red-200' 
                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            }`}
                          >
                            {word.favorite ? <FaHeart className="w-4 h-4" /> : <FaRegStar className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => handleEditWord(word)}
                            className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                          >
                            <FaEdit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteFunnyWord(word.id)}
                            className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                          >
                            <FaTrash className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {todoLists.funnyWords.length === 0 && (
                  <div className="text-center py-8 text-gray-400">
                    <FaLaugh className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No hay palabras graciosas agregadas</p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal para agregar evento */}
      <AnimatePresence>
        {showEventModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white/20 backdrop-blur-md rounded-2xl p-6 max-w-md w-full shadow-2xl border border-white/10"
            >
              <h3 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
                📅 Nuevo Evento
              </h3>
              
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Título del evento..."
                  value={eventForm.title}
                  onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent bg-white/80"
                  autoFocus
                />

                <textarea
                  placeholder="Descripción (opcional)..."
                  value={eventForm.description}
                  onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent bg-white/80 h-20 resize-none"
                />

                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="date"
                    value={eventForm.date}
                    onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
                    className="p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent bg-white/80"
                  />

                  <input
                    type="time"
                    value={eventForm.time}
                    onChange={(e) => setEventForm({ ...eventForm, time: e.target.value })}
                    className="p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent bg-white/80"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <select
                    value={eventForm.type}
                    onChange={(e) => setEventForm({ ...eventForm, type: e.target.value as CalendarEvent['type'] })}
                    className="p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-400 bg-white/80"
                  >
                    <option value="event">Evento</option>
                    <option value="date">Cita</option>
                    <option value="appointment">Reunión</option>
                    <option value="special">Especial</option>
                  </select>

                  <select
                    value={eventForm.priority}
                    onChange={(e) => setEventForm({ ...eventForm, priority: e.target.value as CalendarEvent['priority'] })}
                    className="p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-400 bg-white/80"
                  >
                    <option value="low">Baja</option>
                    <option value="medium">Media</option>
                    <option value="high">Alta</option>
                  </select>
                </div>
              </div>
              
              <div className="flex gap-3 justify-end mt-6">
                <button
                  onClick={() => setShowEventModal(false)}
                  className="px-6 py-3 bg-gray-200/80 text-gray-700 rounded-xl hover:bg-gray-300/80 transition-colors font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAddEvent}
                  disabled={!eventForm.title.trim() || !eventForm.date || isLoading}
                  className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl hover:from-blue-600 hover:to-purple-600 transition-colors disabled:opacity-50 font-medium shadow-lg"
                >
                  {isLoading ? 'Agregando...' : 'Agregar'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal para agregar película/serie */}
      <AnimatePresence>
        {showMovieModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white/20 backdrop-blur-md rounded-2xl p-6 max-w-md w-full shadow-2xl border border-white/10"
            >
              <h3 className="text-xl font-bold bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent mb-4">
                🎬 Nueva Película/Serie
              </h3>
              
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Título..."
                  value={movieForm.title}
                  onChange={(e) => setMovieForm({ ...movieForm, title: e.target.value })}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent bg-white/80"
                  autoFocus
                />

                <div className="grid grid-cols-2 gap-3">
                  <select
                    value={movieForm.type}
                    onChange={(e) => setMovieForm({ ...movieForm, type: e.target.value as MovieSeriesItem['type'] })}
                    className="p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-400 bg-white/80"
                  >
                    <option value="movie">Película</option>
                    <option value="series">Serie</option>
                  </select>

                  <input
                    type="text"
                    placeholder="Género..."
                    value={movieForm.genre}
                    onChange={(e) => setMovieForm({ ...movieForm, genre: e.target.value })}
                    className="p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent bg-white/80"
                  />
                </div>

                <input
                  type="text"
                  placeholder="Plataforma (Netflix, HBO, etc.)..."
                  value={movieForm.platform}
                  onChange={(e) => setMovieForm({ ...movieForm, platform: e.target.value })}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent bg-white/80"
                />

                <textarea
                  placeholder="Notas (opcional)..."
                  value={movieForm.notes}
                  onChange={(e) => setMovieForm({ ...movieForm, notes: e.target.value })}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent bg-white/80 h-20 resize-none"
                />
              </div>
              
              <div className="flex gap-3 justify-end mt-6">
                <button
                  onClick={() => setShowMovieModal(false)}
                  className="px-6 py-3 bg-gray-200/80 text-gray-700 rounded-xl hover:bg-gray-300/80 transition-colors font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAddMovie}
                  disabled={!movieForm.title.trim() || isLoading}
                  className="px-6 py-3 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-xl hover:from-red-600 hover:to-orange-600 transition-colors disabled:opacity-50 font-medium shadow-lg"
                >
                  {isLoading ? 'Agregando...' : 'Agregar'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal para agregar palabra graciosa */}
      <AnimatePresence>
        {showWordModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white/20 backdrop-blur-md rounded-2xl p-6 max-w-md w-full shadow-2xl border border-white/10"
            >
              <h3 className="text-xl font-bold bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent mb-4">
                😄 Nueva Palabra Graciosa
              </h3>
              
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Palabra o frase graciosa..."
                  value={wordForm.word}
                  onChange={(e) => setWordForm({ ...wordForm, word: e.target.value })}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent bg-white/80"
                  autoFocus
                />

                <textarea
                  placeholder="Contexto: cuándo/dónde se dijo..."
                  value={wordForm.context}
                  onChange={(e) => setWordForm({ ...wordForm, context: e.target.value })}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent bg-white/80 h-20 resize-none"
                />
              </div>
              
              <div className="flex gap-3 justify-end mt-6">
                <button
                  onClick={() => setShowWordModal(false)}
                  className="px-6 py-3 bg-gray-200/80 text-gray-700 rounded-xl hover:bg-gray-300/80 transition-colors font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAddWord}
                  disabled={!wordForm.word.trim() || isLoading}
                  className="px-6 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-xl hover:from-yellow-600 hover:to-orange-600 transition-colors disabled:opacity-50 font-medium shadow-lg"
                >
                  {isLoading ? 'Agregando...' : 'Agregar'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};